export type Nullable<T> = T | null;
export type Maybe<T> = T | null | undefined;
export type DeepMaybe<T> = T extends (...args: any[]) => any
  ? Maybe<T>
  : T extends Array<infer U>
  ? Maybe<Array<DeepMaybe<U>>>
  : T extends ReadonlyArray<infer U>
  ? Maybe<ReadonlyArray<DeepMaybe<U>>>
  : T extends object
  ? Maybe<{ [K in keyof T]: DeepMaybe<T[K]> }>
  : Maybe<T>;

/**
 * Wrapper type used to circumvent ESM modules circular dependency issue
 * caused by reflection metadata saving the type of the property.
 * @see https://docs.nestjs.com/recipes/swc#common-pitfalls
 */
export type WrapperType<T> = T;

/**
 * Type representing a class constructor.
 *
 * @template T - The type of the instance that the constructor creates.
 * @template A - The type of the arguments that the constructor accepts. Defaults to an array of unknown types.
 */
export type ClassConstructor<T, A extends Array<unknown> = any[]> = {
  new (...args: A): T;
};

/**
 * Extracts the type of elements from an array type.
 * If the provided type is not an array, it returns `never`.
 *
 * @template T - The array type to extract the element type from.
 *
 * @example ```typescript
 * type Type = Unarray<number[]>; // => `number`
 * type Type = Unarray<string[]>; // => `string`
 * type Type = Unarray<number>;   // => `never`
 * ```
 */
export type Unarray<T> = T extends readonly (infer U)[] ? U : never;

/**
 * Creates a type by making a set of properties K in T optional.
 *
 * @template T - The original type.
 * @template K - The keys of T to set as optional.
 *
 * @example
 * type Foo = { a: number; b: string; c: boolean };
 * type Bar = PartialBy<Foo, 'a' | 'c'>; // { b: string; a?: number; c?: boolean }
 */
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * Type guard function to check if a value is not null.
 *
 * @template T - The type of the value being checked.
 * @param value - The value to check, which can be null or of type T.
 * @returns - Returns true if the value is not null, otherwise false.
 */
export function isNotNull<T>(value: Nullable<T>): value is T {
  return value !== null;
}

/**
 * Converts the type of class to have all properties as optional.
 * @template A - The type of the class constructor arguments.
 * @template T - The type of the class instance.
 * @param cls - The class.
 * @returns - The class with all properties as optional.
 */
export function MakePartial<A extends any[], T extends object>(cls: new (...args: A) => T) {
  const result: new (...args: A) => Partial<T> = cls;
  return result;
}
